import { ISOCurrencyCode } from '@policyfly/protobuf'
import { Formatter, Formatter_DoubleContractOrCurrency_DoubleValue, Formatter_Year_YearRange, RoundingMode } from '@policyfly/protobuf/pfschema'
import { Big } from 'big.js'

import {
  US_STATES,
  ADDRESS_PARTS,
  DECIMAL_RANGE_SEPARATOR,
  ISO_CURRENCY_CODE_STRING_MAP,
} from './constants.js'
import { big, formatNumberInput } from './number.js'
import { countryCodeToString, decimalToString, roundingModeToBigRoundingMode } from './protobuf.js'
import { castArray, isAPIPayloadResponse } from './type.js'

import type { FormatterProgramSettings } from '../types/formatter.js'
import type { Formatter_CustomCurrency } from '@policyfly/protobuf/pfschema'
import type { TSFixMe } from '@policyfly/types/common'

/**
 * Takes in a value and a formatter.
 * Will figure out if the value is a valid decimal range and format each part individually.
 * If not a range, will format the value as is.
 */
function supportDecimalRanges<T extends string> (value: T, formatter: (value: T, ...args: unknown[]) => string): string {
  let parts: T[] = [value]
  if (typeof value === 'string') {
    const valueParts = value.split(DECIMAL_RANGE_SEPARATOR)
    if (valueParts.length === 2) parts = valueParts as T[]
  }
  return parts.map((p) => formatter(p)).join(DECIMAL_RANGE_SEPARATOR)
}

/**
 * If provided an array, will apply the formatter to all items in the array individually.
 * Items will be separated with `', '`.
 */
function supportArrays<T> (value: T | T[], formatter: (value: T) => string): string {
  const items = castArray(value)
  return items.map((item) => formatter(item)).join(', ')
}

/**
 * Formats a current string/number according to the specified format options.
 */
export function formatCurrency (
  programSettings: FormatterProgramSettings,
  value: string | number,
  options: Formatter_CustomCurrency,
): string {
  const parsedValue = formatNumberInput(value)
  let isNegative = parsedValue?.startsWith('-')
  let bigVal = parsedValue ? big(parsedValue).abs() : big(0)
  if (options.convert) {
    bigVal = bigVal.mul(decimalToString(programSettings.getCurrencyInfo()?.exchangeRate))
  }

  const decimalPoints = options.decimalPoints?.value.oneofKind === 'number'
    ? options.decimalPoints.value.number
    : (programSettings.decimals ? 2 : 0)
  const protoRoundingMode = options.roundingMode?.value.oneofKind === 'mode'
    ? options.roundingMode.value.mode
    : programSettings.roundingMode ?? RoundingMode.ROUNDING_MODE_DOWN
  const roundingMode = roundingModeToBigRoundingMode(protoRoundingMode)

  // convert -0 to 0
  const currencyValue = bigVal.toFixed(decimalPoints, roundingMode)
  if (big(currencyValue).eq(0)) isNegative = false

  let returnValue = ''
  if (options.prefix) {
    let prefix = options.prefix
    switch (options.prefix) {
      case 'display':
        /* istanbul ignore next: every ISOCurrencyCode is present in the map */
        prefix = ISO_CURRENCY_CODE_STRING_MAP.get(programSettings.getCurrencyInfo()?.additionalDisplayCurrency ?? ISOCurrencyCode.ISO_CURRENCY_CODE_UNSPECIFIED) ?? ''
        break
      case 'base':
        /* istanbul ignore next: every ISOCurrencyCode is present in the map */
        prefix = ISO_CURRENCY_CODE_STRING_MAP.get(programSettings.getCurrencyInfo()?.currency ?? ISOCurrencyCode.ISO_CURRENCY_CODE_UNSPECIFIED) ?? ''
        break
    }
    returnValue += `${prefix} `
  }
  if (isNegative) returnValue += '-'
  if (options.symbol) returnValue += `${options.symbol}`

  if (options.abbreviate && bigVal.gte(1000)) {
    // abbreviate the currency to 3 digits, plus metric suffix
    const length = currencyValue.length - (decimalPoints ? 1 : 0)
    let index = Math.floor(Math.max(0, length - 3) / 3)
    const truncatedValue = bigVal.div(Math.pow(10, index * 3))
    let abbreviatedValue = truncatedValue.toPrecision(3, roundingMode)
    // check if rounded up to next metric
    if (big(abbreviatedValue).gte(1000)) {
      index++
      abbreviatedValue = truncatedValue.div(1000).toPrecision(3, roundingMode)
    }
    // strip trailing zeroes
    returnValue += Number(abbreviatedValue) + ['', 'k', 'M', 'B', 'T'][index]
  } else {
    returnValue += addCommas(currencyValue)
  }
  return returnValue
}

/**
 * Formats a `yyyy-mm-dd` date string with the current locale.
 * When `options` are provided it replaces the default date format entirely.
 * Returns an empty string for invalid dates.
 *
 * @example
 * formatDate('en-US', '2001-02-03') // "02/03/2001"
 * formatDate('en-US', '2001-02-03', { month: '2-digit', year: 'numeric' }) // "02/2001"
 */
export function formatDate (
  locale: string,
  date?: string | null,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!date || typeof date !== 'string' || date === '-undefined') return ''
  const [year, month, day] = date.split('T')[0].split('-').map((v) => +v)
  const opts = options || {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }
  return new Date(year, month - 1, day).toLocaleDateString(locale, opts)
}

/**
 * Adds comma separators between thousand, million etc. markers.
 */
export function addCommas (x?: string | number | null | Big): string {
  if (!x && x !== 0) return ''
  const parts = x.toString().split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return parts.join('.')
}

export function formatValue (programSettings: FormatterProgramSettings, v: TSFixMe, formatter: Formatter): TSFixMe {
  switch (formatter.name.oneofKind) {
    case 'default':
      return v
    case 'na':
      return v || 'N/A'
    case 'yN':
      if (v === null) return null
      return v ? 'Yes' : 'No'
    case 'yesNoNa':
      switch (v) {
        case true: return 'Yes'
        case false: return 'No'
        default: return 'N/A'
      }
    case 'boolIncluded':
      return v ? 'Included' : 'Not Included'
    case 'phone':
      // oxlint-disable-next-line no-useless-escape
      return v ? v.replace(/\D/g, '').replace(/^(\+1)?(\d{3})(\d{3})(\d{4})$/g, '\+1 ($2) $3-$4') : ''
    case 'date':
      return formatDate(programSettings.locale(), v)
    case 'friendlyDate':
      return formatDate(programSettings.locale(), v, { month: 'long', day: 'numeric', year: 'numeric' })
    case 'customDate': {
      const { options } = formatter.name.customDate
      if (!options?.day && !options?.month && !options?.year) return formatDate(programSettings.locale(), v)
      try {
        return formatDate(programSettings.locale(), v, options as Intl.DateTimeFormatOptions)
      } catch {
        console.warn(`Invalid customDate options: ${JSON.stringify(options)}`)
        return formatDate(programSettings.locale(), v)
      }
    }
    case 'noDecimalCurrency': {
      if (v === 'N/A' || v === '') return v
      return supportDecimalRanges(v, (r) => formatCurrency(programSettings, r, { decimalPoints: { value: { oneofKind: 'number', number: 0 } }, symbol: '$' }))
    }
    case 'noDecimalCurrencyUsd': {
      return supportDecimalRanges(v, (r) => formatCurrency(programSettings, r, { decimalPoints: { value: { oneofKind: 'number', number: 0 } }, prefix: 'USD' }))
    }
    case 'decimalCurrency': {
      if (v === 'N/A' || v === '') return v
      return supportDecimalRanges(v, (r) => formatCurrency(programSettings, r, { decimalPoints: { value: { oneofKind: 'number', number: 2 } }, symbol: '$' }))
    }
    case 'currency': {
      if (v === 'N/A' || v === '') return v
      return supportDecimalRanges(v, (r) => formatCurrency(programSettings, r, { symbol: '$' }))
    }
    case 'multiPartCurrency': {
      let parts = (v || '').split('/')
      parts = parts.map((part: string) => formatValue(programSettings, part, Formatter.create({ name: { oneofKind: 'currency', currency: {} } })))
      return parts.join('/')
    }
    case 'currencyIgnoreZero': {
      const value = supportDecimalRanges(v, (r) => formatValue(programSettings, r, Formatter.create({ name: { oneofKind: 'currency', currency: {} } })))
      return (value === '$0.00' || value === '$0') ? '' : value
    }
    case 'addCurrencySymbol':
      return v ? `$${v}` : ''
    case 'addCommas':
      return supportDecimalRanges(v, (r) => addCommas(r))
    case 'number':
      return supportDecimalRanges(v, (r) => addCommas(r || 0))
    case 'properCase':
      return v ? v.split(' ').map((w: string) => w.charAt(0).toUpperCase() + w.substring(1).toLowerCase()).join(' ') : ''
    case 'onOff':
      return v === true || v === 'on' ? 'On' : 'Off'
    case 'address':
      if (!v) return ''
      // support already transformed addresses
      if (typeof v === 'string') return v
      // support both protobuf & JSON data structures
      return ['address1', ...ADDRESS_PARTS.filter((part) => !['geocoderLocationType', 'googlePlaceId'].includes(part))]
        .map((part) => {
          if (part in v) {
            const val = v[part]
            // convert protobuf country enum to friendly string
            if (part === 'country' && typeof val === 'number') return countryCodeToString(val)
            if (val) return isAPIPayloadResponse(val) ? val.v : val
          }
          return null
        })
        .filter((p) => !!p)
        .join(', ')
    case 'lossExperienceYear': {
      const index = ['Current Year', 'Prior Year', 'Second Prior', 'Third Prior', 'Fourth Prior'].findIndex((y) => y === v)
      const effectiveDate = programSettings.getEffectiveDate()
      const applicationYear = (effectiveDate ? new Date(effectiveDate) : new Date()).getFullYear()
      return index > -1 ? applicationYear - index : applicationYear
    }
    case 'currentYear':
      return +v === new Date().getFullYear() ? `${v} (Current Year)` : v
    case 'state': {
      const state = US_STATES.find((s) => s.abbreviation === v)
      return state ? state.name : v
    }
    case 'sterlingRiskQuoteLimit': {
      if (typeof v !== 'string') return ''
      const millions = v.substr(0, v.length - 6)
      return `$${millions}M Limit`
    }
    case 'apdCancellationReason': {
      const cancellationRequests = programSettings.cancellationRequests
      if (!cancellationRequests) {
        console.warn('Cannot display cancellation request reasons as they do not exist')
        return null
      }
      if (cancellationRequests.type.oneofKind !== 'selection') {
        console.warn('Cancellation request must be `selection`')
        return null
      }
      const reason = cancellationRequests.type.selection.params!.reasons.find((r) => r.value === v) || { text: null }
      return reason.text
    }
    case 'bcapdSoleProprietor':
      return v === 'Individual Company' ? 'Yes' : 'No'
    case 'srArmedGuards':
      return v ? 'Armed' : 'Unarmed'
    case 'attachmentName': {
      if (!v) return ''
      const metadata = programSettings.getAttachmentMetadata(v)
      if (!metadata) return 'Attachment Not Found'
      return metadata.previewName || metadata.name || 'Untitled Attachment'
    }
    case 'mapValue': {
      const { valueMap, defaultValue } = formatter.name.mapValue
      return supportArrays(v, (item) => {
        return item in valueMap
          ? valueMap[item]
          : defaultValue?.value.oneofKind === 'original'
            ? String(item)
            : defaultValue?.value.oneofKind === 'string'
              ? defaultValue.value.string
              : ''
      })
    }
    case 'percentage': {
      const { accuracy, decimalPercentage = false, roundingMode = RoundingMode.ROUNDING_MODE_HALF_UP, invert = false } = formatter.name.percentage
      return supportDecimalRanges(v, (r) => {
        const initialValue = Big(r || 0).mul(decimalPercentage ? 100 : 1)
        const value = invert
          ? initialValue.abs().minus(100).abs().mul(initialValue.s)
          : initialValue
        const acc = accuracy?.value.oneofKind === 'number'
          ? accuracy.value.number
          : null
        return `${value.toFixed(acc ?? undefined, roundingModeToBigRoundingMode(roundingMode))}%`
      })
    }
    case 'suffix': {
      const { singular, plural } = formatter.name.suffix
      const value = addCommas(v)
      if (!value) return ''
      return value === '1' ? `1 ${singular}` : `${value} ${plural}`
    }
    case 'doubleContractOrCurrency': {
      const { doubleValues } = formatter.name.doubleContractOrCurrency
      const mappedValues = doubleValues.map((d) => {
        switch (d) {
          case Formatter_DoubleContractOrCurrency_DoubleValue.DOUBLE_VALUE_DOUBLE: return 'double'
          case Formatter_DoubleContractOrCurrency_DoubleValue.DOUBLE_VALUE_NULL: return null
          /* istanbul ignore next: defensive default — all enum members are handled above */
          default: return null
        }
      })
      if (mappedValues?.includes(v)) return 'Double Contract Deductible'
      return formatValue(programSettings, v, Formatter.create({ name: { oneofKind: 'currency', currency: {} } }))
    }
    case 'customCurrency': {
      const params = formatter.name.customCurrency
      return supportDecimalRanges(v, (r) => formatCurrency(programSettings, r, params))
    }
    case 'year': {
      if (!v) return ''
      const { yearRange, yearOffset } = formatter.name.year
      let year = +v
      if (yearOffset) year += yearOffset
      switch (yearRange) {
        case Formatter_Year_YearRange.YEAR_RANGE_NEXT: return `${year} - ${year + 1}`
        case Formatter_Year_YearRange.YEAR_RANGE_PREVIOUS: return `${year - 1} - ${year}`
        default: return String(year)
      }
    }
    default: {
      const invalidFormatter: undefined = formatter.name.oneofKind
      console.warn(`Invalid Formatter: ${invalidFormatter}`)
      return v
    }
  }
}
